import { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Divider, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useBalanceTableFiltersStore } from 'store/balanceTableFilters';
import { useMerchantsStore } from 'store/merchants';
import type { TComponent } from 'utils/types/component';

import style from './ExportModal.module.scss';

type TExportModalProps = {
    isModalVisible: boolean;
    onCloseModal: () => void;
    handleExport: (currencies: string[]) => void;
};

const ExportModal: TComponent<TExportModalProps> = ({
    isModalVisible,
    onCloseModal,
    handleExport,
}) => {
    const { t } = useTranslation();

    const [isAllChecked, setIsAllChecked] = useState(true);
    const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);

    const body = useBalanceTableFiltersStore('body');
    const currentMerchantIdentity = body.merchantIdentities?.[0];
    const currentMerchant = useMerchantsStore('merchants').find(
        (merchant) => merchant.identity === currentMerchantIdentity,
    );
    const currenciesOptions = useMemo(() => {
        return currentMerchant?.currencyCodes?.map((currency) => ({
            label: currency,
            value: currency,
        }));
    }, [currentMerchant]);

    const onCheckAllChange = () => {
        setIsAllChecked(!isAllChecked);

        if (!isAllChecked) {
            setSelectedCurrencies(
                currenciesOptions?.map((currency) => currency.value) ?? [],
            );
        } else {
            setSelectedCurrencies([]);
        }
    };

    const handleSelectCurrencies = (values: string[]) => {
        setSelectedCurrencies(values);
        setIsAllChecked(values.length === (currenciesOptions?.length ?? 0));
    };

    const handleCloseModal = () => {
        setIsAllChecked(true);
        setSelectedCurrencies([]);
        onCloseModal();
    };

    useEffect(() => {
        if (isModalVisible && currenciesOptions) {
            setSelectedCurrencies(
                currenciesOptions.map((currency) => currency.value),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalVisible]);

    return (
        <Modal
            open={isModalVisible}
            onOk={() => handleExport(selectedCurrencies)}
            onCancel={handleCloseModal}
            closable={true}
            footer={[
                <Button
                    key='export'
                    type='primary'
                    onClick={() => handleExport(selectedCurrencies)}
                    disabled={!selectedCurrencies.length}
                    className={style.exportButton}
                >
                    {t('balance.exportModal.button')}
                </Button>,
            ]}
        >
            <div>
                <Typography.Title level={3}>
                    {t('balance.exportModal.title')} {currentMerchant?.name}
                </Typography.Title>
                <div>
                    <Checkbox
                        onChange={onCheckAllChange}
                        checked={isAllChecked}
                    >
                        {t('balance.exportModal.checkAll')}
                    </Checkbox>
                    <Divider />
                    <Typography.Text>
                        {t('balance.exportModal.subtitle')}
                    </Typography.Text>
                    <Checkbox.Group
                        options={currenciesOptions}
                        value={selectedCurrencies}
                        onChange={handleSelectCurrencies}
                        className={style.checkboxGroup}
                    />
                    <Divider />
                </div>
            </div>
        </Modal>
    );
};

export default ExportModal;
